import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, 
  LayoutDashboard, 
  Zap, 
  FileText, 
  Users, 
  Shield,
  Command,
  X,
  Search,
  ArrowRight,
  Sparkles,
  Bot,
  Wallet,
  ExternalLink
} from 'lucide-react';

// Floating Dock Navigation - 2026 Trend
export function FloatingDock() {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredItem, setHoveredItem] = useState(null);

  const navItems = [
    { 
      name: 'Home', 
      href: '/', 
      icon: Home, 
      color: 'from-blue-500 to-cyan-500',
      description: 'Landing page'
    },
    { 
      name: 'Dashboard', 
      href: '/dashboard', 
      icon: LayoutDashboard, 
      color: 'from-cyan-500 to-teal-500',
      description: 'DePIN Analytics',
      badge: 'LIVE'
    },
    { 
      name: 'x402', 
      href: '/x402', 
      icon: Zap, 
      color: 'from-purple-500 to-pink-500',
      description: 'Machine Payments',
      badge: 'NEW'
    },
    { 
      name: 'Whitepaper', 
      href: '/whitepaper', 
      icon: FileText, 
      color: 'from-orange-500 to-amber-500',
      description: 'Documentation'
    },
    { 
      name: 'Team', 
      href: '/team', 
      icon: Users, 
      color: 'from-green-500 to-emerald-500',
      description: 'Our crew'
    },
    { 
      name: 'Apply', 
      href: '/apply', 
      icon: Shield, 
      color: 'from-red-500 to-rose-500',
      description: 'Join security team'
    },
  ];

  // Command palette keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
      if (e.key === 'Escape') {
        setCommandPaletteOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const filteredItems = navItems.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Floating Dock - Bottom Center */}
      <motion.div 
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
      >
        <motion.div 
          className="relative flex items-center gap-1 px-2 py-2 rounded-2xl border border-white/10 shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8), rgba(30, 41, 59, 0.8))',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
          onMouseEnter={() => setIsExpanded(true)}
          onMouseLeave={() => setIsExpanded(false)}
        >
          {/* Glow effect */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 blur-xl opacity-50" />
          
          {/* Navigation Items */}
          <div className="relative flex items-center gap-1">
            {navItems.map((item, index) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                  className="relative group"
                >
                  <motion.div
                    className={`relative flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-300 ${
                      active 
                        ? `bg-gradient-to-br ${item.color} shadow-lg` 
                        : 'hover:bg-white/10'
                    }`}
                    whileHover={{ scale: 1.15, y: -8 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    <Icon className={`w-5 h-5 ${active ? 'text-white' : 'text-gray-400 group-hover:text-white'}`} />
                    
                    {/* Badge */}
                    {item.badge && (
                      <span className={`absolute -top-1 -right-1 px-1.5 py-0.5 text-[8px] font-bold rounded-full ${
                        item.badge === 'NEW' 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-green-500 text-white'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </motion.div>

                  {/* Tooltip */}
                  <AnimatePresence>
                    {hoveredItem === index && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.9 }}
                        transition={{ duration: 0.15 }}
                        className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-3 py-2 rounded-lg whitespace-nowrap pointer-events-none"
                        style={{
                          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255,255,255,0.1)'
                        }}
                      >
                        <div className="text-white text-sm font-semibold">{item.name}</div>
                        <div className="text-gray-400 text-xs">{item.description}</div>
                        {/* Arrow */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-slate-800" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Link>
              );
            })}

            {/* Divider */}
            <div className="w-px h-8 bg-white/10 mx-1" />

            {/* Command Palette Trigger */}
            <motion.button
              onClick={() => setCommandPaletteOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="command-palette-trigger"
            >
              <Command className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500 hidden sm:inline">⌘K</span>
            </motion.button>
          </div>
        </motion.div>
      </motion.div>

      {/* Command Palette Modal */}
      <AnimatePresence>
        {commandPaletteOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
              onClick={() => setCommandPaletteOpen(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-xl z-[101]"
            >
              <div 
                className="mx-4 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
                  backdropFilter: 'blur(20px)',
                }}
              >
                {/* Search Input */}
                <div className="flex items-center gap-3 px-4 py-4 border-b border-white/10">
                  <Search className="w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search pages, actions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-transparent text-white placeholder-gray-500 outline-none text-lg"
                    autoFocus
                  />
                  <button 
                    onClick={() => setCommandPaletteOpen(false)}
                    className="p-1 hover:bg-white/10 rounded-lg transition"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                {/* Results */}
                <div className="max-h-[400px] overflow-y-auto">
                  {/* Pages Section */}
                  <div className="px-3 py-2">
                    <div className="text-xs text-gray-500 uppercase tracking-wider px-2 py-2">
                      Pages
                    </div>
                    {filteredItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setCommandPaletteOpen(false)}
                          className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-colors group"
                        >
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="text-white font-medium flex items-center gap-2">
                              {item.name}
                              {item.badge && (
                                <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                                  item.badge === 'NEW' ? 'bg-purple-500/20 text-purple-400' : 'bg-green-500/20 text-green-400'
                                }`}>
                                  {item.badge}
                                </span>
                              )}
                            </div>
                            <div className="text-gray-500 text-sm">{item.description}</div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-white transition-colors" />
                        </Link>
                      );
                    })}
                  </div>

                  {/* Quick Actions */}
                  <div className="px-3 py-2 border-t border-white/5">
                    <div className="text-xs text-gray-500 uppercase tracking-wider px-2 py-2">
                      Quick Actions
                    </div>
                    <button 
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-colors group"
                      onClick={() => {
                        window.open('https://jup.ag/swap/SOL-HVjfYg327vUiYrATQfd74NdkNEqQQk4NxhGtMAnMF7CE', '_blank');
                        setCommandPaletteOpen(false);
                      }}
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-white font-medium">Buy $BODY</div>
                        <div className="text-gray-500 text-sm">Trade on Jupiter</div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-white transition-colors" />
                    </button>
                    <a 
                      href="https://zetal.io" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-colors group"
                      onClick={() => setCommandPaletteOpen(false)}
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-white font-medium">ZETAL.IO</div>
                        <div className="text-gray-500 text-sm">Main website</div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-white transition-colors" />
                    </a>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-gray-400">↑↓</kbd>
                      Navigate
                    </span>
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-gray-400">↵</kbd>
                      Select
                    </span>
                    <span className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-gray-400">esc</kbd>
                      Close
                    </span>
                  </div>
                  <span className="text-cyan-500">⚡ SkyPilot</span>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default FloatingDock;
